/* eslint-disable no-param-reassign */
/* eslint-disable prefer-destructuring */
/* eslint-disable prefer-rest-params */
import {
  AppBar,
  Box,
  Button,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import "./index.scss";
import { Link, navigate } from "gatsby";
import cx from "classnames";
import { useCompanyInfo } from "../../../hooks";
import { BUSINESS_TYPE, DEFAULT_BROKER_DETAILS } from "../../../commons/enum";
import { useLoanApplicationLayout } from "../../../context/loan-application-layout-context";
import StandardButton from "../../../components/StandardButton/index.tsx";
import {
  GunnLogo,
  GunnLogoShorten,
  GunnLogoWhite,
  NoImage,
} from "../../../images";
import { primaryColor } from "../../../scss/colors.module.scss";
import { logout } from "../../../utils/auth";

const PREFIX = "LoanApplicationLayout";

const classes = {
  appBar: `${PREFIX}-appBar`,
  appBarContent: `${PREFIX}-appBarContent`,
  content: `${PREFIX}-content`,
  brokerInformationSection: `${PREFIX}-brokerInformationSection`,
  brokerInformationContainerTitle: `${PREFIX}-brokerInformationContainerTitle`,
  underlineHighlighted: `${PREFIX}-underlineHighlighted`,
  button: `${PREFIX}-button`,
  clientLoginButton: `${PREFIX}-clientLoginButton`,
  quickNavigationButtonsContainer: `${PREFIX}-quickNavigationButtonsContainer`,
  footer: `${PREFIX}-footer`,
  footerNavigation: `${PREFIX}-footerNavigation`,
  footerLinkContainer: `${PREFIX}-footerLinkContainer`,
  footerButton: `${PREFIX}-footerButton`,
  contactInformationWrapper: `${PREFIX}-contactInformationWrapper`,
  contactInformationContainer: `${PREFIX}-contactInformationContainer`,
  workingHours: `${PREFIX}-workingHours`,
  title: `${PREFIX}-title`,
  contactInformation: `${PREFIX}-contactInformation`,
  siteBuildInformation: `${PREFIX}-siteBuildInformation`,
  companyInformation: `${PREFIX}-companyInformation`,
  licenseInformation: `${PREFIX}-licenseInformation`,
  siteBuiltBy: `${PREFIX}-siteBuiltBy`,
  mobileNoPadding: `${PREFIX}-mobileNoPadding`,
  hiddenOnMobile: `${PREFIX}-hiddenOnMobile`,
  shownOnMobile: `${PREFIX}-shownOnMobile`,
  marginBottom16: `${PREFIX}-marginBottom16`,
  brokerInformationContainer: `${PREFIX}-brokerInformationContainer`,
  brokerInformationWrapper: `${PREFIX}-brokerInformationWrapper`,
  logoButton: `${PREFIX}-logoButton`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.appBar}`]: {
    height: "96px",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    display: "flex",
    justifyContent: "center",
    marginBottom: "16px",
    [theme.breakpoints.down("xs")]: {
      height: "54px",
    },
  },
  [`& .${classes.appBarContent}`]: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  [`& .${classes.content}`]: {
    minHeight: "100vh",
    flexGrow: "1",
  },
  [`& .${classes.brokerInformationSection}`]: {
    paddingTop: "48px",
    paddingBottom: "48px",
  },
  [`& .${classes.brokerInformationContainerTitle}`]: {
    fontWeight: "700",
    fontSize: "38px",
    lineHeight: "46px",
  },
  [`& .${classes.underlineHighlighted}`]: {
    textDecoration: "underline",
    textDecorationColor: primaryColor,
    textDecorationThickness: "3px",
  },
  [`& .${classes.button}`]: {
    textTransform: "none",
    padding: "14.5px 20px",
    "&:hover": {
      color: "inherit",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "8px 16px",
    },
  },
  [`& .${classes.clientLoginButton}`]: {
    "&:hover": {
      color: "#FFF",
    },
  },
  [`& .${classes.quickNavigationButtonsContainer}`]: {
    display: "flex",
    padding: "16px",
    height: "80px",
    [theme.breakpoints.down("sm")]: {
      height: "54px",
      padding: "8px",
    },
  },
  [`& .${classes.footer}`]: {
    background: "var(--secondaryColor)",
    padding: "54px 0",
    color: "var(--textOnSecondary)",
  },
  [`& .${classes.footerNavigation}`]: {
    paddingTop: "54px",
    paddingBottom: "54px",
    flexGrow: "1",
    marginRight: "15%",
    [theme.breakpoints.down("sm")]: {
      paddingTop: "0",
      paddingBottom: "0",
      marginRight: "0",
      display: "flex",
      justifyContent: "center",
      marginBottom: "24px",
      flexDirection: "column",
    },
  },
  [`& .${classes.footerLinkContainer}`]: {
    display: "flex",
    justifyContent: "space-between",
    "& span": {
      fontSize: "16px",
    },
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
    },
  },
  [`& .${classes.footerButton}`]: {
    padding: 0,
    textTransform: "none",
    color: "var(--textOnSecondary)",
    width: "105px",
    height: "45px",
    display: "initial",
    borderBottom: `5px solid ${primaryColor}`,
    borderRadius: 0,
    "&:hover": {
      color: "inherit",
    },
  },
  [`& .${classes.contactInformationWrapper}`]: {
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
    },
  },
  [`& .${classes.contactInformationContainer}`]: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "10px 0",
    minWidth: "245px",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
    },
  },
  [`& .${classes.workingHours}`]: {
    marginBottom: "10px",
  },
  [`& .${classes.title}`]: {
    fontWeight: "bold",
    marginBottom: "12px",
  },
  [`& .${classes.contactInformation}`]: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  [`& .${classes.siteBuildInformation}`]: {
    background: "rgba(5, 19, 21, 1)",
    padding: "24px 0",
  },
  [`& .${classes.companyInformation}`]: {
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
    },
  },
  [`& .${classes.licenseInformation}`]: {
    color: "rgba(255, 255, 255, 1)",
  },
  [`& .${classes.siteBuiltBy}`]: {
    color: "rgba(255, 255, 255, 1)",
    minWidth: "245px",
  },
  [`& .${classes.mobileNoPadding}`]: {
    [theme.breakpoints.down("xs")]: {
      paddingLeft: "0",
      paddingRight: "0",
    },
  },
  [`& .${classes.hiddenOnMobile}`]: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  [`& .${classes.shownOnMobile}`]: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "initial",
    },
  },
  [`& .${classes.marginBottom16}`]: {
    marginBottom: "16px",
  },
  [`& .${classes.brokerInformationContainer}`]: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    gap: "32px",
    [theme.breakpoints.between(980, "md")]: {
      gap: "16px",
    },
    [theme.breakpoints.down(980)]: {
      gap: "16px",
      flexDirection: "column-reverse",
      alignItems: "flex-end",
    },
  },
  [`& .${classes.brokerInformationWrapper}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: "48px",
    [theme.breakpoints.down(980)]: {
      gap: "24px",
      flexDirection: "row",
      justifyContent: "space-between",
    },
  },
  [`& .${classes.logoButton}`]: {
    cursor: "pointer",
  },
}));

function LoanApplicationLayout({ children, location }) {
  const { data: companyInfo = {} } = useCompanyInfo();
  const creditGuideFileUrl =
    companyInfo?.homepageDetails?.creditGuideFileUrl || "";
  const [isSavingAndLoggingOut, setIsSavingAndLoggingOut] = useState(false);

  const { pathname } = location;
  const { isRouteGateActive, saveCallback } = useLoanApplicationLayout();
  // On `/loan-application`, the page can immediately redirect to a pre-loan route.
  // Hide this layout chrome during that decision to prevent a visible flash.
  const shouldHideLayout = useMemo(
    () => pathname === "/loan-application/" && isRouteGateActive,
    [pathname, isRouteGateActive]
  );
  const shouldShowBrokerInfo = !pathname.includes("/privacy"); // do not show broker info in privacy page
  const phoneNumber =
    companyInfo?.businessPhoneNumber ||
    companyInfo?.adminDetails?.user?.phoneNumber ||
    DEFAULT_BROKER_DETAILS.phoneNumber;
  const email =
    companyInfo?.customerSupportEmail ||
    companyInfo?.adminDetails?.user?.email ||
    DEFAULT_BROKER_DETAILS.supportEmail;

  useEffect(() => {
    if (!companyInfo?.businessType) {
      return;
    }
    if (companyInfo.businessType !== BUSINESS_TYPE.BROKER) {
      window.location.href = "/";
    }
  }, [companyInfo?.businessType]);

  useEffect(() => {
    // Hotjar tracking script
    const hotjarId = process.env.GATSBY_HOTJAR_ID;
    const hotjarVersion = process.env.GATSBY_HOTJAR_VERSION;
    if (!hotjarId || !hotjarVersion) {
      return;
    }

    if (typeof window !== "undefined" && !window.hj) {
      ((h, o, t, j, a, r) => {
        h.hj =
          h.hj ||
          // eslint-disable-next-line func-names
          function () {
            (h.hj.q = h.hj.q || []).push(arguments);
          };
        h._hjSettings = {
          hjid: parseInt(hotjarId, 10),
          hjsv: parseInt(hotjarVersion, 10),
        };
        a = o.getElementsByTagName("head")[0];
        r = o.createElement("script");
        r.async = 1;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a.appendChild(r);

        // Hotjar script loaded
      })(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");
    }
  }, []);

  if (shouldHideLayout) {
    return children;
  }

  return (
    <Root className="loan-app-page-container">
      <AppBar color="transparent" position="static" className={classes.appBar}>
        <Grid container size={12}>
          <Grid size={1} />
          <Grid container className={classes.appBarContent} size={10}>
            <Box
              className={classes.logoButton}
              onClick={() => navigate("/loan-application")}
            >
              <img
                src={companyInfo?.theme?.logoDarkUrl || GunnLogo}
                alt="app-logo"
                className="loan-app-logo-desktop"
              />
              <img
                src={
                  companyInfo?.theme?.iconDarkUrl ||
                  companyInfo?.theme?.logoDarkUrl ||
                  GunnLogoShorten
                }
                alt="app-logo"
                className="loan-app-logo-mobile"
              />
            </Box>
            <Box
              className={cx(
                classes.quickNavigationButtonsContainer,
                classes.mobileNoPadding
              )}
              sx={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <StandardButton
                variant="outlined"
                loading={isSavingAndLoggingOut}
                onClick={async () => {
                  setIsSavingAndLoggingOut(true);
                  try {
                    if (saveCallback) {
                      try {
                        // Race the save callback against a 2 second timeout
                        const timeoutPromise = new Promise((resolve) => {
                          setTimeout(() => resolve(), 2000);
                        });
                        await Promise.race([saveCallback(), timeoutPromise]);
                      } catch {
                        // Silently fail - user can still log out
                      }
                    }
                    await logout("/login");
                  } finally {
                    setIsSavingAndLoggingOut(false);
                  }
                }}
                classes={{
                  borderColor: "var(--secondaryColor, #162029)",
                  color: "#131313",
                  textTransform: "none",
                  letterSpacing: "0.4px",
                  padding: "8px 22px",
                  "&:hover": {
                    borderColor: "var(--secondaryColor, #162029)",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Save & Log Out
              </StandardButton>
              <Button
                variant="contained"
                onClick={() => {
                  navigate("/");
                }}
                className={cx(
                  classes.button,
                  "loan-app-font-size-14",
                  "loan-app-font-weight-semibold"
                )}
                sx={{
                  backgroundColor: "var(--secondaryColor, #162029)",
                  color: "white !important",
                  textTransform: "none",
                  letterSpacing: "0.4px",
                  padding: "8px 22px",
                  "&:hover": {
                    backgroundColor: "var(--secondaryColor, #162029)",
                  },
                }}
              >
                Login App
              </Button>
            </Box>
          </Grid>
          <Grid size={1} />
        </Grid>
      </AppBar>
      <Grid container className={classes.content}>
        <Grid size={1} />
        <Grid size={10}>
          {shouldShowBrokerInfo && (
            <Container
              maxWidth={false}
              className={cx(
                classes.brokerInformationSection,
                "loan-app-desktop-display"
              )}
            >
              <Grid container>
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="flex-start"
                  spacing={2}
                  size={{
                    xs: 5,
                    md: 6,
                  }}
                >
                  <Grid>
                    <Typography
                      className={classes.brokerInformationContainerTitle}
                    >
                      Quick Online Application, <br /> That Get’s You Answers
                      Fast!
                    </Typography>
                  </Grid>
                  <Grid>
                    <Typography
                      component="span"
                      className="loan-app-font-size-18"
                    >
                      <Typography
                        component="span"
                        variant="inherit"
                        className={cx(
                          classes.underlineHighlighted,
                          "loan-app-font-size-18"
                        )}
                      >
                        (This does not
                      </Typography>{" "}
                      effect your credit score)
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  size={{
                    xs: 7,
                    md: 6,
                  }}
                >
                  <Box className={classes.brokerInformationContainer}>
                    <Box className={classes.brokerInformationWrapper}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                        rowGap="8px"
                      >
                        <Typography className="loan-app-font-size-18">
                          <strong>
                            {companyInfo?.adminDetails?.user?.firstName}{" "}
                            {companyInfo?.adminDetails?.user?.lastName}
                          </strong>
                        </Typography>
                        <Typography className="loan-app-font-size-18">
                          Mortgage Broker
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                        rowGap="8px"
                      >
                        <Typography className="loan-app-font-size-18">
                          E: {email}
                        </Typography>
                        <Typography className="loan-app-font-size-18">
                          M: {phoneNumber}
                        </Typography>
                      </Box>
                    </Box>
                    <Box width="fit-content">
                      <div className="loan-app-broker-avatar-wrapper">
                        <img
                          src={companyInfo?.adminDetails?.imageUrl || NoImage}
                          alt="broker-avater"
                          className="loan-app-broker-avatar"
                        />
                        <div className="loan-app-broker-avatar-shadow-box__inner" />
                        <div className="loan-app-broker-avatar-shadow-box__outer" />
                      </div>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          )}
          <Grid container className="loan-app-content-container">
            {children}
          </Grid>
        </Grid>
        <Grid size={1} />
      </Grid>
      <Grid container>
        <Grid container className={classes.footer} size={12}>
          <Grid
            size={{
              xs: 2,
              sm: 1,
            }}
          />
          <Grid
            container
            size={{
              xs: 8,
              sm: 10,
            }}
          >
            <Grid
              container
              size={{
                xs: 12,
                sm: 8,
              }}
            >
              <Box className={classes.footerNavigation}>
                <img
                  src={companyInfo?.theme?.logoUrl || GunnLogoWhite}
                  alt="app-logo-white"
                  className="loan-app-logo-white-desktop"
                />
                <img
                  src={
                    companyInfo?.theme?.iconUrl ||
                    companyInfo?.theme?.logoUrl ||
                    GunnLogoWhite
                  }
                  alt="app-logo-white"
                  className="loan-app-logo-white-mobile"
                />
                <Box className={cx(classes.footerLinkContainer)}>
                  <Button
                    component={Link}
                    to="/loan-application"
                    className={classes.footerButton}
                  >
                    Home
                  </Button>
                  <Button
                    component={Link}
                    to="/signup"
                    className={classes.footerButton}
                  >
                    Signup
                  </Button>
                  <Button
                    component={Link}
                    to="/login"
                    className={classes.footerButton}
                  >
                    Login
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid
              container
              className={classes.contactInformationWrapper}
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <Box className={classes.contactInformationContainer}>
                <Box
                  className={cx(classes.shownOnMobile, classes.marginBottom16)}
                >
                  <Typography
                    className={cx(
                      "loan-app-font-size-18",
                      "loan-app-text-white"
                    )}
                  >
                    Just a phone call away <br /> if you need help.
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    className={cx(
                      classes.title,
                      "loan-app-font-size-24",
                      "loan-app-text-white",
                      classes.hiddenOnMobile
                    )}
                  >
                    Get In Touch
                  </Typography>
                  <Typography
                    className={cx(
                      classes.workingHours,
                      "loan-app-font-size-18",
                      "loan-app-text-white"
                    )}
                  >
                    Weekdays 8 AM - 8 PM AEST
                  </Typography>
                  <Typography
                    className={cx(
                      classes.workingHours,
                      "loan-app-font-size-18",
                      "loan-app-text-white"
                    )}
                  >
                    Saturday 10 AM - 4 PM
                  </Typography>
                  <Typography
                    className={cx(
                      classes.workingHours,
                      "loan-app-font-size-18",
                      "loan-app-text-white"
                    )}
                  >
                    Sunday We Enjoy Australia
                  </Typography>
                </Box>
                <Box className={classes.contactInformation}>
                  <Typography
                    className={cx(
                      "loan-app-font-size-18",
                      "loan-app-text-white"
                    )}
                  >
                    <b>Call:</b> {phoneNumber}
                  </Typography>
                  <Typography
                    className={cx(
                      "loan-app-font-size-18",
                      "loan-app-text-white"
                    )}
                  >
                    <b>Email:</b> {email}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Grid
            size={{
              xs: 2,
              sm: 1,
            }}
          />
        </Grid>
        <Grid container className={classes.siteBuildInformation} size={12}>
          <Grid
            size={{
              xs: 2,
              sm: 1,
            }}
          />
          <Grid
            container
            className={classes.companyInformation}
            size={{
              xs: 8,
              sm: 7,
            }}
          >
            <Typography
              className={cx(
                classes.licenseInformation,
                "loan-app-font-size-12",
                "loan-app-font-weight-light",
                "loan-app-text-white",
                "loan-app-text-uppercase",
                classes.hiddenOnMobile
              )}
            >
              ALL APPLICANTS ARE SUBJECT TO ELIGIBILITY AND ASSESSMENT{" "}
              {`${companyInfo?.name || DEFAULT_BROKER_DETAILS.companyName}`} PTY
              LTD | ABN{" "}
              {`${
                companyInfo?.companyNumber ||
                DEFAULT_BROKER_DETAILS.companyNumber
              }`}{" "}
              | AUSTRALIAN CREDIT LICENCE{" "}
              {`${
                companyInfo?.creditRepNumber ||
                DEFAULT_BROKER_DETAILS.creditRepNumber
              }`}
            </Typography>
            <Typography
              className={cx(
                classes.licenseInformation,
                "loan-app-font-size-12",
                "loan-app-font-weight-light",
                "loan-app-text-white",
                "loan-app-text-uppercase",
                classes.shownOnMobile
              )}
            >
              {`${companyInfo?.name || DEFAULT_BROKER_DETAILS.companyName}`} PTY
              LTD | ABN{" "}
              {`${
                companyInfo?.companyNumber ||
                DEFAULT_BROKER_DETAILS.companyNumber
              }`}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            size={{
              xs: false,
              sm: 3,
            }}
          >
            <Box className={classes.siteBuiltBy}>
              <Typography
                component={Link}
                to="/loan-application/privacy"
                className={cx(
                  "loan-app-font-size-16",
                  "loan-app-font-weight-light",
                  "loan-app-text-white"
                )}
              >
                Privacy & Policy
              </Typography>
              {creditGuideFileUrl && (
                <>
                  &nbsp;&nbsp;&nbsp;
                  <Typography
                    component="a"
                    href={creditGuideFileUrl}
                    target="_blank"
                    className={cx(
                      "loan-app-font-size-16",
                      "loan-app-font-weight-light",
                      "loan-app-text-white"
                    )}
                  >
                    Credit Guide
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
          <Grid
            size={{
              xs: 2,
              sm: 1,
            }}
          />
        </Grid>
      </Grid>
    </Root>
  );
}

export default LoanApplicationLayout;
